import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import { startCase } from 'lodash'
import { Trans, useTranslation } from 'react-i18next'

import SelectField from '../../../FormLegacy/FormFieldsV2/SelectField'
import {
  updateBuildingIntegration,
  disconnectBuildingIntegration,
} from '../../../../reducers/buildingSelectorReducer'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import API from '../../../../api/api'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import {
  getPmsTypeOptions,
  INTEGRATION_TYPES,
  DISCONNECTABLE_INTEGRATIONS,
  pmsValidationSchema,
  pmsIntegrationInitialValues,
  getPmsIntegrationInitialValues,
  getPmsIntegrationType,
  getCompanyId,
  getPropertyId,
} from '../helpers'

import OrganisationIntegrationModal from './OrganisationIntegrationModal'
import PayRentConfigModal from './PayRentConfigModal'
import PmsIntegrationConfiguration from './PmsIntegrationConfiguration'

const PmsIntegration = ({
  selectedBuilding,
  updateBuildingIntegration = () => {},
  disconnectBuildingIntegration = () => {},
}) => {
  const { t } = useTranslation(['organisation', 'common'])
  const pmsTypeOptions = getPmsTypeOptions(t)
  const [fetchPMLoading, setFetchPMLoading] = useState(false)
  const [fetchCurrentPropertyLoading, setFetchCurrentPropertyLoading] = useState(false)
  const [fetchPropertiesLoading, setFetchPropertiesLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showPayRentModal, setShowPayRentModal] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)
  const [pmCompanyOptions, setPMCompanyOptions] = useState([])
  const [propertyOptions, setPropertyOptions] = useState([])
  const { displaySnackbar } = useSnackbar()

  const {
    entrataPropertyId,
    entrataManagementId,
    yardiPropertyId,
    yardiManagementId,
    yardiPaymentId,
    realPagePropertyId,
    realPageManagementId,
    rentManagerPropertyId,
    rentManagerManagementId,
    resmanPropertyId,
    resmanManagementId,
  } = selectedBuilding

  const isLoading =
    fetchPMLoading ||
    fetchCurrentPropertyLoading ||
    fetchPropertiesLoading ||
    isSubmitting ||
    isDisconnecting

  const isYardi = Boolean(yardiPropertyId && yardiManagementId)
  const isRentManager = Boolean(rentManagerPropertyId && rentManagerManagementId)

  const hasConnectedIntegration = Boolean(
    (entrataPropertyId && entrataManagementId) ||
      isYardi ||
      (realPagePropertyId && realPageManagementId) ||
      (rentManagerPropertyId && rentManagerManagementId) ||
      (resmanPropertyId && resmanManagementId),
  )

  const connectedPmsType = getPmsIntegrationType(selectedBuilding)

  const fetchPMCompanyList = async (integrationName) => {
    if (!integrationName) return
    setFetchPMLoading(true)
    try {
      const { data = [] } = await API.fetchPMCompanyList(integrationName)
      setPMCompanyOptions(data.map(({ id, name }) => ({ id, text: name })))
    } catch ({ message }) {
      setPMCompanyOptions([])
      displaySnackbar(message, true)
    } finally {
      setFetchPMLoading(false)
    }
  }

  const fetchCurrentProperty = async (integrationName, companyId, propertyId) => {
    setFetchCurrentPropertyLoading(true)
    try {
      switch (integrationName) {
        case INTEGRATION_TYPES.YARDI: {
          const { data } = await API.getYardiProperty(companyId, propertyId)
          setPropertyOptions([{ id: data.id, text: data.name }])
          break
        }
        case INTEGRATION_TYPES.ENTRATA: {
          const { data } = await API.getEntrataProperty(companyId, propertyId)
          setPropertyOptions([{ id: data.id, text: data.name }])
          break
        }
        case INTEGRATION_TYPES.RENT_MANAGER: {
          const { data } = await API.getRentManagerProperty(companyId, propertyId)
          setPropertyOptions([{ id: data.id, text: data.name }])
          break
        }
        default:
          break
      }
    } catch ({ message, response }) {
      const { data } = response
      setPropertyOptions([])
      displaySnackbar(data && data.message ? data.message : message, true)
    } finally {
      setFetchCurrentPropertyLoading(false)
    }
  }

  const fetchProperties = async (integrationName, companyId) => {
    setFetchPropertiesLoading(true)
    try {
      switch (integrationName) {
        case INTEGRATION_TYPES.YARDI: {
          const { data = [] } = await API.fetchYardiProperties(companyId)
          setPropertyOptions(data.map(({ id, name }) => ({ id, text: name })))
          break
        }
        case INTEGRATION_TYPES.ENTRATA: {
          const { data = [] } = await API.fetchEntrataProperties(companyId)
          setPropertyOptions(
            data.map(({ entrataPropertyId, name }) => ({ id: entrataPropertyId, text: name })),
          )
          break
        }
        case INTEGRATION_TYPES.RENT_MANAGER: {
          const { data = [] } = await API.fetchRentManagerProperties(companyId)
          setPropertyOptions(data.map(({ id, name }) => ({ id, text: name })))
          break
        }
        default:
          break
      }
    } catch ({ message, response }) {
      const { data } = response
      setPropertyOptions([])
      displaySnackbar(data && data.message ? data.message : message, true)
    } finally {
      setFetchPropertiesLoading(false)
    }
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true)
    try {
      const { type, company, property } = values
      let requestData

      switch (type) {
        case INTEGRATION_TYPES.YARDI:
          requestData = { yardiManagementId: company, yardiPropertyId: property }
          break
        case INTEGRATION_TYPES.ENTRATA:
          requestData = { entrataManagementId: company, entrataPropertyId: property }
          break
        case INTEGRATION_TYPES.REAL_PAGE:
          requestData = { realPageManagementId: company, realPagePropertyId: property }
          break
        case INTEGRATION_TYPES.RENT_MANAGER:
          requestData = { rentManagerManagementId: company, rentManagerPropertyId: property }
          break
        default:
          break
      }

      if (requestData) {
        await updateBuildingIntegration(requestData, selectedBuilding.id, type)
        displaySnackbar(t('organisation:places.integrations.keyless.toasts.saved'))
      }
    } catch {
      displaySnackbar(t('organisation:places.integrations.keyless.toasts.saveFailed'), true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onDisconnect = async () => {
    if (!connectedPmsType) return
    setIsDisconnecting(true)
    try {
      await disconnectBuildingIntegration(selectedBuilding.id, connectedPmsType)
      setPMCompanyOptions([])
      setPropertyOptions([])
      displaySnackbar(t('organisation:places.integrations.pms.toasts.disconnected'))
    } catch (error) {
      displaySnackbar(
        error?.response?.data?.message ||
          error?.message ||
          t('organisation:places.integrations.pms.toasts.disconnectFailed'),
        true,
      )
    } finally {
      setShowDisconnectConfirm(false)
      setIsDisconnecting(false)
    }
  }

  const onCreateNewCompany = (company, values, setValues = () => {}) => {
    setValues({ ...values, property: null, company: company.id })
    setPMCompanyOptions([...pmCompanyOptions, { id: company.id, text: company.name }])
    fetchProperties(values.type, company.id)
  }

  useEffect(() => {
    if (hasConnectedIntegration) {
      const type = getPmsIntegrationType(selectedBuilding)
      if (type && type !== INTEGRATION_TYPES.RESMAN) {
        fetchPMCompanyList(type)
        fetchCurrentProperty(
          type,
          getCompanyId(selectedBuilding, type),
          getPropertyId(selectedBuilding, type),
        )
      }
    }
  }, [selectedBuilding, hasConnectedIntegration])

  return (
    <div className={`ctp-integration ${isLoading ? 'is-loading' : ''}`}>
      <h3 className="pms-integration__title">{t('organisation:places.integrations.pms.title')}</h3>
      <Formik
        enableReinitialize
        initialValues={getPmsIntegrationInitialValues(selectedBuilding)}
        validationSchema={pmsValidationSchema}
        onSubmit={onSubmit}
      >
        {({ handleChange, setValues, handleSubmit, values, isValid }) => {
          const hasConnectedSelectedIntegration =
            (entrataPropertyId && entrataManagementId && values.type === 'entrata') ||
            (yardiPropertyId && yardiManagementId && values.type === 'yardi') ||
            (realPagePropertyId && realPageManagementId && values.type === 'real-page') ||
            (rentManagerPropertyId && rentManagerManagementId && values.type === 'rent-manager') ||
            (resmanPropertyId && resmanManagementId && values.type === 'resman')

          return (
            <React.Fragment>
              <form onSubmit={handleSubmit}>
                <div className="ctp-integration__content">
                  <SelectField
                    name="type"
                    disabled={hasConnectedIntegration}
                    options={pmsTypeOptions}
                    placeholder={t(
                      'organisation:places.integrations.pms.integrationType.placeholder',
                    )}
                    label={t('organisation:places.integrations.pms.integrationType.label')}
                    onChange={(e) => {
                      setValues({ ...pmsIntegrationInitialValues, type: e.target.value })
                      fetchPMCompanyList(e.target.value)
                    }}
                    value={values.type}
                  />

                  <PmsIntegrationConfiguration
                    values={values}
                    setValues={setValues}
                    handleChange={handleChange}
                    hasConnectedIntegration={hasConnectedIntegration}
                    pmCompanyOptions={pmCompanyOptions}
                    propertyOptions={propertyOptions}
                    fetchProperties={fetchProperties}
                    setShowModal={setShowModal}
                  />

                  <button
                    disabled={!isValid || hasConnectedSelectedIntegration}
                    className={`button ${hasConnectedSelectedIntegration ? 'button-gray' : 'button-primary'}`}
                    type="submit"
                  >
                    {hasConnectedSelectedIntegration
                      ? t('organisation:places.integrations.pms.connectedWith', {
                          type: startCase(values.type),
                        })
                      : t('common:actions.save')}
                  </button>

                  {hasConnectedSelectedIntegration &&
                    DISCONNECTABLE_INTEGRATIONS.includes(values.type) && (
                      <button
                        type="button"
                        className="button button-transparent ctp-integration__disconnect"
                        disabled={isDisconnecting}
                        onClick={() => setShowDisconnectConfirm(true)}
                      >
                        {t('organisation:places.integrations.pms.disconnect.button')}
                      </button>
                    )}
                </div>
              </form>
              {showModal && (
                <OrganisationIntegrationModal
                  type={values.type}
                  onClose={() => setShowModal(false)}
                  onCreated={(company) => onCreateNewCompany(company, values, setValues)}
                />
              )}
              {showDisconnectConfirm && (
                <ConfirmPopup
                  isOpen
                  title={t('organisation:places.integrations.pms.disconnect.confirmTitle')}
                  body={
                    <p>
                      <Trans
                        i18nKey="organisation:places.integrations.pms.disconnect.body"
                        values={{ type: startCase(connectedPmsType) }}
                        components={[<strong key="type" />]}
                      />
                    </p>
                  }
                  confirmText={t('organisation:places.integrations.pms.disconnect.button')}
                  cancelText={t('common:actions.cancel')}
                  isLoading={isDisconnecting}
                  confirmCallback={onDisconnect}
                  cancelCallback={() => setShowDisconnectConfirm(false)}
                />
              )}
            </React.Fragment>
          )
        }}
      </Formik>
      {((isYardi && yardiPaymentId) || isRentManager) && (
        <button
          type="button"
          className="button button-primary"
          onClick={() => setShowPayRentModal(true)}
        >
          {t('organisation:places.integrations.pms.payRentConfigButton')}
        </button>
      )}
      {showPayRentModal && (
        <PayRentConfigModal
          onClose={() => setShowPayRentModal(false)}
          buildingId={selectedBuilding.id}
          isRentManager={isRentManager}
        />
      )}
    </div>
  )
}

PmsIntegration.propTypes = {
  selectedBuilding: PropTypes.object.isRequired,
  updateBuildingIntegration: PropTypes.func,
  disconnectBuildingIntegration: PropTypes.func,
}

export default connect(null, { updateBuildingIntegration, disconnectBuildingIntegration })(
  PmsIntegration,
)
